import React from "react";
import { FormattedDate } from "react-intl";
import useHover from "@react-hook/hover";
import useSwitch from "@react-hook/switch";
import { Link } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { Dialog } from "@syos-music/common";
import { useAuth } from "@syos-music/auth";

import {
  MenuItem,
  MenuList,
  ListItemText,
  Tooltip,
  Checkbox,
  CircularProgress,
  Stack,
  ListItemIcon,
  Typography,
  Box,
  Collapse,
} from "@mui/material";
import {
  CloudRefresh as ForceRegenIcon,
  CloudDownload as DownloadIcon,
  CloudOffOutline as NoDownloadIcon,
  Delete as DeleteIcon,
  Eye as PreviewIcon,
  ContentCopy as CopyIcon,
  Pencil as EditIcon,
  Help as FeedbackIcon,
} from "mdi-material-ui";

import WebSiteLink from "components/common/WebSiteLink";
import MouthpieceViewer from "components/mouthpiece/MouthpieceViewer";

import SoundShapingInventories from "./SoundShapingInventories";
import SoundShapingDesign from "./SoundShapingDesign";
import SoundShapingColorSelector from "./SoundShapingColorSelector";
import SoundShapingCopyInfos from "./SoundShapingCopyInfos";
import RemoveSoundShaping from "./RemoveSoundShaping";

const LAPIPROD_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:4001"
    : "https://lapiprod.syos.co";

const TEN_DAYS = 10 * 24 * 60 * 60 * 1000;

const FRAGMENTS = {
  order: gql`
    fragment OrderSidebar_SoundShaping_ListItem_order on Order {
      id

      ...Orders_OrderSidebarSoundShaping_CopyInfos_Order
    }

    ${SoundShapingCopyInfos.fragments.order}
  `,
  soundShaping: gql`
    fragment OrderSidebar_SoundShaping_ListItem_soundShaping on SoundShaping {
      id
      color
      isFinalMouthpiece
      isPrinted
      lapiprodId
      createdAt
      updatedAt

      mouthpieceFileDesign

      mouthpieceRef {
        id
        name
        type

        legacyMouthpiece {
          id

          stlFile {
            id
            url
            name
          }
        }

        saxophoneMouthpiece {
          id

          files {
            design
            stl {
              id
              url
            }
            sldprt {
              id
              url
            }

            gcode {
              id
              color
              file {
                id
                url
              }
            }
          }
        }

        clarinetMouthpiece {
          id

          files {
            design
            stl {
              id
              url
            }
            sldprt {
              id
              url
            }

            gcode {
              id
              color
              file {
                id
                url
              }
            }
          }
        }
      }

      ...SoundShapingInventories_SoundShaping
      ...SoundShapingDesign_SoundShaping
      ...Orders_OrderSidebarSoundShaping_CopyInfos_SoundShaping
      ...Orders_SoundShapingColorSelector_SoundShaping
    }

    ${SoundShapingInventories.fragments.soundShaping}
    ${SoundShapingDesign.fragments.soundShaping}
    ${SoundShapingCopyInfos.fragments.soundShaping}
    ${SoundShapingColorSelector.fragments.soundShaping}
  `,
};

const UPDATE_SOUNDSHAPING_MUTATION = gql`
  mutation (
    $orderId: ID!
    $soundShapingId: ID!
    $isFinalMouthpiece: Boolean
    $isPrinted: Boolean
    $color: String
  ) {
    UpdateSoundShaping(
      orderId: $orderId
      soundShapingId: $soundShapingId
      isFinalMouthpiece: $isFinalMouthpiece
      isPrinted: $isPrinted
      color: $color
    ) {
      order {
        id
        soundShapings {
          id
          isFinalMouthpiece
          isPrinted
          color
        }
      }
    }
  }
`;

const FORCE_SOUNDSHAPING_REGEN_MUTATION = gql`
  mutation ($soundShapingId: ID!) {
    ForceSoundShapingRegen(soundShapingId: $soundShapingId) {
      soundShaping {
        id

        ...OrderSidebar_SoundShaping_ListItem_soundShaping
      }
    }
  }

  ${FRAGMENTS.soundShaping}
`;

const CREATE_LAPIPROD_PRINT_TASK_MUTATION = gql`
  mutation ($soundShapingId: ID!) {
    CreateLapiprodPrintTask(soundShapingId: $soundShapingId) {
      soundShaping {
        id
        lapiprodId
      }
    }
  }
`;

export default function SoundShapingListItem({
  order,
  soundShaping,
  count,
  ...props
}) {
  const { hasRole } = useAuth();
  const forceRegenAllowed = hasRole("ADMIN") || hasRole("SYOS_DESIGNER");
  const regenStuck =
    new Date(soundShaping.updatedAt).getTime() < Date.now() - TEN_DAYS;

  const rootRef = React.useRef();
  const isHover = useHover(rootRef, { enterDelay: 400 });

  const [updateSoundShaping, { loading: loadingUpdateSoundShaping }] =
    useMutation(UPDATE_SOUNDSHAPING_MUTATION, {
      variables: {
        orderId: order.id,
        soundShapingId: soundShaping.id,
      },
    });

  const [forceSoundShapingRegen, { loading: loadingForceSoundShapingRegen }] =
    useMutation(FORCE_SOUNDSHAPING_REGEN_MUTATION, {
      variables: { soundShapingId: soundShaping.id },
    });

  const [createLapiprodPrintTask, { loading: loadingCreateLapiprodPrintTask }] =
    useMutation(CREATE_LAPIPROD_PRINT_TASK_MUTATION, {
      variables: { soundShapingId: soundShaping.id },
    });

  const loading = loadingUpdateSoundShaping || loadingForceSoundShapingRegen;

  const [mouthpieceViewerOpen, toggleMouthpieceViewerOpen] = useSwitch();

  let mouthpieceFile;
  if (soundShaping.mouthpieceRef.saxophoneMouthpiece) {
    mouthpieceFile = soundShaping.mouthpieceRef.saxophoneMouthpiece.files.find(
      ({ design }) => design === soundShaping.mouthpieceFileDesign
    );
  }

  if (soundShaping.mouthpieceRef.clarinetMouthpiece) {
    mouthpieceFile = soundShaping.mouthpieceRef.clarinetMouthpiece.files.find(
      ({ design }) => design === soundShaping.mouthpieceFileDesign
    );
  }

  let stlFile;
  if (mouthpieceFile) {
    stlFile = mouthpieceFile.stl;
  } else if (soundShaping.mouthpieceRef.legacyMouthpiece) {
    stlFile = soundShaping.mouthpieceRef.legacyMouthpiece.stlFile;
  }

  let gcodeFile;
  if (mouthpieceFile) {
    gcodeFile = mouthpieceFile.gcode?.find(
      (gcode) => gcode.color === soundShaping.color
    )?.file;
  }

  return (
    <Box ref={rootRef} {...props}>
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        sx={{
          padding: (t) => t.spacing(0.5, 1),
          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <Checkbox
          onChange={(evt, isFinalMouthpiece) =>
            updateSoundShaping({ variables: { isFinalMouthpiece } })
          }
          checked={soundShaping.isFinalMouthpiece}
          disabled={loading}
          sx={{ p: 0 }}
        />

        <Checkbox
          onChange={(evt, isPrinted) =>
            updateSoundShaping({ variables: { isPrinted } })
          }
          checked={soundShaping.isPrinted}
          disabled={loading}
          sx={{ p: 0 }}
        />

        <SoundShapingInventories soundShaping={soundShaping} />

        <Tooltip placement="top" title={soundShaping.mouthpieceRef.name}>
          <Typography noWrap sx={{ flexGrow: 1 }}>
            {count}. {soundShaping.mouthpieceRef.name}
          </Typography>
        </Tooltip>

        <SoundShapingDesign
          soundShaping={soundShaping}
          sx={{ flexShrink: 0 }}
        />

        <SoundShapingColorSelector
          soundShaping={soundShaping}
          onChange={(color) => updateSoundShaping({ variables: { color } })}
          disabled={loading}
        />
      </Stack>

      <Collapse in={isHover}>
        <MenuList>
          <MenuItem>
            <ListItemIcon></ListItemIcon>
            <ListItemText
              primary={
                <FormattedDate
                  year="numeric"
                  month="long"
                  day="2-digit"
                  hour="2-digit"
                  minute="2-digit"
                  value={soundShaping.createdAt}
                />
              }
            />
          </MenuItem>

          <MenuItem
            onClick={createLapiprodPrintTask}
            disabled={loadingCreateLapiprodPrintTask}
          >
            <ListItemIcon>
              {loadingCreateLapiprodPrintTask && (
                <CircularProgress size="20px" />
              )}
            </ListItemIcon>
            <ListItemText primary="Envoyer dans Lapiprod" />
          </MenuItem>

          {soundShaping.lapiprodId && (
            <MenuItem
              component="a"
              href={`${LAPIPROD_URL}/dashboard?search.id=${soundShaping.lapiprodId}&search.orderId=${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon />
              <ListItemText primary="Ouvrir dans Lapiprod" />
            </MenuItem>
          )}

          {!!soundShaping.mouthpieceRef.legacyMouthpiece && (
            <MenuItem
              component={Link}
              to={`/mouthpieces/legacy/${soundShaping.mouthpieceRef.legacyMouthpiece.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              <ListItemText primary="Éditer le bec" />
            </MenuItem>
          )}

          {forceRegenAllowed && mouthpieceFile && (
            <MenuItem onClick={forceSoundShapingRegen}>
              <ListItemIcon>
                <ForceRegenIcon />
              </ListItemIcon>
              <ListItemText primary="Re-générer le bec" />
            </MenuItem>
          )}

          {mouthpieceFile?.sldprt && (
            <MenuItem
              component="a"
              href={mouthpieceFile.sldprt.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>
                <DownloadIcon />
              </ListItemIcon>
              <ListItemText primary="Télécharger le fichier SolidWorks" />
            </MenuItem>
          )}

          {stlFile ? (
            <MenuItem
              component="a"
              href={stlFile.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>
                <DownloadIcon />
              </ListItemIcon>
              <ListItemText primary="Télécharger le fichier STL" />
            </MenuItem>
          ) : soundShaping.mouthpieceRef.legacyMouthpiece ? (
            <MenuItem
              component={Link}
              to={`/mouthpieces/legacy/${soundShaping.mouthpieceRef.legacyMouthpiece.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>
                <NoDownloadIcon />
              </ListItemIcon>
              <ListItemText primary="Pas fichier STL" />
            </MenuItem>
          ) : regenStuck ? (
            forceRegenAllowed && (
              <MenuItem onClick={forceSoundShapingRegen}>
                <ListItemIcon>
                  <ForceRegenIcon />
                </ListItemIcon>
                <ListItemText primary="Générer le bec" />
              </MenuItem>
            )
          ) : (
            <MenuItem dense>
              <ListItemIcon>
                <CircularProgress size="20px" />
              </ListItemIcon>
              <ListItemText primary="Génération en cours…" />
            </MenuItem>
          )}

          {gcodeFile && (
            <MenuItem
              component="a"
              href={gcodeFile.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ListItemIcon>
                <DownloadIcon />
              </ListItemIcon>
              <ListItemText primary="Télécharger le fichier Gcode" />
            </MenuItem>
          )}

          {stlFile && (
            <MenuItem onClick={toggleMouthpieceViewerOpen}>
              <ListItemIcon>
                <PreviewIcon />
              </ListItemIcon>
              <ListItemText primary="Prévisualiser" />

              <div
                // Stop propagation to parent MenuItem which will re-open the dialog
                onClick={(e) => e.stopPropagation()}
                onMouseOver={(e) => e.stopPropagation()}
              >
                <Dialog
                  open={mouthpieceViewerOpen}
                  onClose={toggleMouthpieceViewerOpen}
                  maxWidth="md"
                >
                  <Dialog.Title>{soundShaping.mouthpieceRef.name}</Dialog.Title>
                  <MouthpieceViewer url={stlFile.url} />
                </Dialog>
              </div>
            </MenuItem>
          )}

          <SoundShapingCopyInfos order={order} soundShaping={soundShaping}>
            {({ copy }) => (
              <MenuItem onClick={copy}>
                <ListItemIcon>
                  <CopyIcon />
                </ListItemIcon>
                <ListItemText primary="Copier les infos" />
              </MenuItem>
            )}
          </SoundShapingCopyInfos>

          <MenuItem
            component={WebSiteLink}
            locale={order.user?.profile?.locale ?? "en"}
            to={`/user/orders/${order.id}/feedback/${soundShaping.id}`}
          >
            <ListItemIcon>
              <FeedbackIcon />
            </ListItemIcon>
            <ListItemText primary="Ouvrir le formulaire de feedback" />
          </MenuItem>

          <RemoveSoundShaping
            orderId={order.id}
            soundShapingId={soundShaping.id}
          >
            {({ open }) => (
              <MenuItem onClick={open}>
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText primary="Supprimer" />
              </MenuItem>
            )}
          </RemoveSoundShaping>
        </MenuList>
      </Collapse>
    </Box>
  );
}

SoundShapingListItem.fragments = FRAGMENTS;
